import { times, includes } from "lodash"
import { getPositionSet } from "helpers/comparison"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getMissingDataFiles, betasSubQuery } from "selectors/missingDataFiles"
import { getClassifications, classificationsQuery, generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { getFileName } from "modules/comparison/selectors/"
import { MODEL_METADATA_QUERY } from "resources/models"
import createModule from "../createModule"
import view from "./view"
import { getCurrency } from "./selectors"
import { getData, getMissingEquityItemsDates } from "./selectors/data"
import { getMetrics, getGroups, getSelectedMetric, getSelectedGroup, getPositionsDisplayType } from "./selectors/metrics"
import { getItems } from "./selectors/items"
import { getDetailsItems, getDetailsData, getPositionsSorting, getPositionsPagination, getTableDownloadData } from "./selectors/details"
import { getStatus } from "./selectors/status"
import { Creators } from "./model"

export default createModule({
  view,
  externalProps: (state, props) => ({
    selectedGroup: getSelectedGroup(state, props),
    missingDataFiles: getMissingDataFiles(state, props),
    currency: getCurrency(state, props),
    classifications: getClassifications(state, props),
  }),
  required: ["account", "modelId", "positionSets", "startDate", "endDate", "selectedDate"],
  data: [...times(5, (n) => {
    return {
      resource: `compareCompositionPositionSet${n}`,
      as: null,
      passthrough: n > 0,
      url: ({ modelId, positionSets, startDate, endDate, base, viewType, missingDataFiles, currency }) => {
        if (!positionSets[n] || positionSets[n].uploading || !missingDataFiles) {
          return null
        }

        const positionSet = getPositionSet(positionSets, n, startDate, { composition: true, currency })

        // If a segment is applied on the positionSet, use that segment on the base
        const baseCopy = { ...base }
        if (positionSet.segment) {
          baseCopy.segment = positionSet.segment
        }

        const isActive = viewType === VIEW_TYPES.ACTIVE

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${startDate}${endDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(baseCopy) : ""}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $groupBy: ContributorGroupType!) {
                model (id: $modelId) {
                  simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
                    composition (scaleFormat: PERCENT_EQUITY_GMV) {
                      date
                      gmv
                      referenceEquity
                      positionsCount
                      modeledGmv
                      summaryStats {
                        average {
                          marketCapitalization
                        }
                      }
                      concentration {
                        min
                        max
                      }
                      longShortComposition: compositionBy (groupBy: $groupBy) {
                        id
                        description
                        economicExposure
                      }
                    }
                    ${betasSubQuery(missingDataFiles, ["date"])}
                  }
                }
              }`,
          variables: {
            modelId,
            positionSet,
            from: startDate,
            to: endDate,
            base: isActive ? [baseCopy] : undefined,
            groupBy: isActive ? "OVERWEIGHT_UNDERWEIGHT" : "LONG_SHORT",
          },
        }
      },
    }
  }), ...times(5, (n) => {
    return {
      resource: `compareCompositionPositionData${n}`,
      as: null,
      passthrough: true,
      url: ({ modelId, positionSets, base, viewType, selectedGroup, selectedDate, currency, classifications }) => {
        if (!positionSets[n] || positionSets[n].uploading || !selectedGroup) {
          return null
        }

        const positionSet = getPositionSet(positionSets, n, selectedDate, { composition: true, currency })
        const { groupBy, tier, groupById } = getQueryGroupBy(selectedGroup, viewType === VIEW_TYPES.ACTIVE)

        let contributionBy = ""
        let compositionQueryVariables = ""
        if (!includes(["LONG_SHORT", "OVERWEIGHT_UNDERWEIGHT"], groupBy)) {
          compositionQueryVariables = ", $groupBy: ContributorGroupType! $groupById: String, $tier: String"
          contributionBy = `
            compositionBy (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
              id
              description
              economicExposure
            }
          `
        }

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${selectedDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(base) : ""}${groupBy}${tier}${groupById}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date! ${compositionQueryVariables}) {
                model (id: $modelId) {
                  simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
                    composition (scaleFormat: PERCENT_EQUITY_GMV) {
                      positions {
                        id
                        assetClass
                        economicExposure
                        basePercentEquity: baseEconomicExposure
                        positionPercentEquity: positionSetEconomicExposure
                        description
                        isUnmodeled
                        currency
                        country
                        modelProviderId
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      }
                      ${contributionBy}
                    }
                  }
                }
              }`,
          variables: {
            modelId,
            positionSet,
            from: selectedDate,
            to: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            ...(contributionBy && { groupBy, tier, groupById }),
          },
        }
      },
    }
  }), {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    data: getData(state, props),
    metrics: getMetrics(state, props),
    items: getItems(state, props),
    detailsItems: getDetailsItems(state, props),
    detailsData: getDetailsData(state, props),
    status: getStatus(state),
    selectedMetric: getSelectedMetric(state, props),
    groups: getGroups(state, props),
    positionsDisplayType: getPositionsDisplayType(state),
    positionsSorting: getPositionsSorting(state, props),
    positionsPagination: getPositionsPagination(state, props),
    missingEquityItemsDates: getMissingEquityItemsDates(state, props),
    fileName: getFileName(state, props),
    tableDownloadData: getTableDownloadData(state, props),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
